import { ModuleTypeEnum, ModuleColumnDefinition } from '../module';
import { STANDARD_COLUMN_WIDTHS, getFieldTypeWidth } from '../column-widths';
import { FieldTypeEnum } from '../custom-field.types';
import { Resource } from '../resource';

/**
 * Creates standard column definitions for the Resource module
 */
export function createResourceStandardColumns(): ModuleColumnDefinition[] {
  return [
    {
      id: 'name',
      name: 'name' as keyof Resource,
      label: 'Resource Name',
      description: 'Name of the resource',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: true,
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '200px',
      minWidth: '200px',
    },
    {
      id: 'code',
      name: 'code' as keyof Resource,
      label: 'Code',
      description: 'Unique code for the resource',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: false,
      order: 1,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'type',
      name: 'type' as keyof Resource,
      label: 'Resource Type',
      description: 'Type category of the resource',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 2,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      width: '150px',
      minWidth: '150px',
    },
    {
      id: 'status',
      name: 'status' as keyof Resource,
      label: 'Status',
      description: 'Current status of the resource',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 3,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'description',
      name: 'description' as keyof Resource,
      label: 'Description',
      description: 'Description of the resource',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 4,
      fieldType: FieldTypeEnum.enum.TEXT,
      width: '200px',
      minWidth: '200px',
    },
    {
      id: 'capacity',
      name: 'capacity' as keyof Resource,
      label: 'Capacity',
      description: 'Maximum capacity of the resource',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 5,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '100px',
      minWidth: '100px',
    },
    {
      id: 'location',
      name: 'location' as keyof Resource,
      label: 'Location',
      description: 'Physical location of the resource',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 6,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '150px',
      minWidth: '150px',
    },
    {
      id: 'costPerHour',
      name: 'costPerHour' as keyof Resource,
      label: 'Cost per Hour',
      description: 'Internal cost rate per hour',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 7,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'billPerHour',
      name: 'billPerHour' as keyof Resource,
      label: 'Bill per Hour',
      description: 'Billing rate per hour',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 8,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '120px',
      minWidth: '120px',
    },
    {
      id: 'tags',
      name: 'tags' as keyof Resource,
      label: 'Tags',
      description: 'Tags associated with the resource',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 9,
      fieldType: FieldTypeEnum.enum.STRING,
      width: '150px',
      minWidth: '150px',
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof Resource,
      label: 'Created Date',
      description: 'Date when resource was created',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 10,
      fieldType: FieldTypeEnum.enum.DATETIME,
      width: '150px',
      minWidth: '150px',
    },
    {
      id: 'updatedAt',
      name: 'updatedAt' as keyof Resource,
      label: 'Updated Date',
      description: 'Date when resource was last updated',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 11,
      fieldType: FieldTypeEnum.enum.DATETIME,
      width: '150px',
      minWidth: '150px',
    },
  ];
}

/**
 * Default standard column definitions for the Resource module
 */
export const RESOURCE_STANDARD_COLUMNS: ModuleColumnDefinition[] = createResourceStandardColumns();

/**
 * Helper function to get all available columns for the resource module,
 * combining standard and custom fields
 */
export function getResourceColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = createResourceStandardColumns().map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible: visibilityPreferences?.[column.id] !== undefined 
      ? !!visibilityPreferences[column.id]
      : column.isVisible
  }));
  
  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (fieldType: string): { width: string, minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return { width: '180px', minWidth: '180px' };
      case FieldTypeEnum.enum.TEXT:
        return { width: '200px', minWidth: '200px' };
      case FieldTypeEnum.enum.NUMBER:
        return { width: '120px', minWidth: '120px' };
      case FieldTypeEnum.enum.BOOLEAN:
        return { width: '120px', minWidth: '120px' };
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return { width: '150px', minWidth: '150px' };
      case FieldTypeEnum.enum.DROPDOWN:
        return { width: '150px', minWidth: '150px' };
      default:
        return { width: '150px', minWidth: '150px' };
    }
  };
  
  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType);
    
    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
        ? !!visibilityPreferences[`custom_${field.fieldName}`]
        : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    };
  }) as ModuleColumnDefinition[];
  
  // Combine both types of columns
  return [...standardColumns, ...customColumns];
}

/**
 * Import/Export column mapping for resources
 */
export const RESOURCE_IMPORT_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', required: true, example: 'Conference Room A' },
  { key: 'code', label: 'Code', required: false, example: 'CONF-A' },
  { key: 'description', label: 'Description', required: false, example: 'Main conference room' },
  { key: 'type', label: 'Type', required: false, example: 'Meeting Room' },
  { key: 'status', label: 'Status', required: false, example: 'AVAILABLE' },
  { key: 'capacity', label: 'Capacity', required: false, example: '12' },
  { key: 'location', label: 'Location', required: false, example: 'Building 1' },
  { key: 'costPerHour', label: 'Cost Per Hour', required: false, example: '25.00' },
  { key: 'billPerHour', label: 'Bill Per Hour', required: false, example: '75.00' },
  { key: 'tags', label: 'Tags', required: false, example: 'projector;whiteboard' },
];

/**
 * Get module type for column definitions
 */
export function getResourceModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.TEAM;
} 